const KNOWN_SECTION_TYPES = new Set([
  "pageTitle",
  "alertsTabBar",
  "filterBar",
  "alertListHeader",
]);

const KNOWN_TAB_WIDGET_TYPES = new Set(["alertsTab"]);

const KNOWN_FILTER_WIDGET_TYPES = new Set([
  "dateRange",
  "selectFilter",
  "multiSelectFilter",
]);

const KNOWN_LIST_HEADER_WIDGET_TYPES = new Set([
  "bulkSelect",
  "listTitle",
  "bulkActions",
  "search",
  "pagination",
  "sort",
]);

const KNOWN_BULK_ACTION_KEYS = new Set([
  "clearSelection",
  "selectedCount",
  "delete",
  "markAsRead",
  "markAsUnread",
]);

const requiredString = (value) => typeof value === "string" && value.trim().length > 0;

export function validateAlertsLayout(layout) {
  const errors = [];

  if (!layout || typeof layout !== "object") {
    return { valid: false, errors: ["Layout must be an object"] };
  }

  if (typeof layout.schemaVersion !== "number") {
    errors.push("schemaVersion must be a number");
  }

  if (!requiredString(layout.pageId)) {
    errors.push("pageId is required");
  }

  if (!requiredString(layout.module)) {
    errors.push("module is required");
  }

  if (!Array.isArray(layout.sections) || layout.sections.length === 0) {
    errors.push("sections must be a non-empty array");
  } else {
    const hasTabBar = layout.sections.some((section) => section?.type === "alertsTabBar");
    if (!hasTabBar) {
      errors.push("sections must include an alertsTabBar section");
    }

    layout.sections.forEach((section, sectionIndex) => {
      if (!requiredString(section?.id)) {
        errors.push(`sections[${sectionIndex}].id is required`);
      }

      if (!requiredString(section?.type)) {
        errors.push(`sections[${sectionIndex}].type is required`);
      } else if (!KNOWN_SECTION_TYPES.has(section.type)) {
        errors.push(`sections[${sectionIndex}] has unknown type "${section.type}"`);
      }

      if (section?.type === "alertsTabBar") {
        if (!Array.isArray(section.widgets) || section.widgets.length === 0) {
          errors.push(`sections[${sectionIndex}].widgets must be a non-empty array`);
        } else {
          section.widgets.forEach((widget, widgetIndex) => {
            if (!requiredString(widget?.tabKey)) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}].tabKey is required`,
              );
            }
            if (widget?.type && !KNOWN_TAB_WIDGET_TYPES.has(widget.type)) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}] has unknown type "${widget.type}"`,
              );
            }
          });
        }
      }

      if (section?.type === "filterBar") {
        if (!Array.isArray(section.widgets)) {
          errors.push(`sections[${sectionIndex}].widgets must be an array`);
        } else {
          section.widgets.forEach((widget, widgetIndex) => {
            if (!requiredString(widget?.filterKey)) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}].filterKey is required`,
              );
            }
            if (!requiredString(widget?.type)) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}].type is required`,
              );
            } else if (!KNOWN_FILTER_WIDGET_TYPES.has(widget.type)) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}] has unknown type "${widget.type}"`,
              );
            }
            if (
              widget?.type === "selectFilter" &&
              (!Array.isArray(widget.options) || widget.options.length === 0)
            ) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}].options must be a non-empty array`,
              );
            }
          });
        }
      }

      if (section?.type === "alertListHeader") {
        if (!Array.isArray(section.widgets) || section.widgets.length === 0) {
          errors.push(`sections[${sectionIndex}].widgets must be a non-empty array`);
        } else {
          section.widgets.forEach((widget, widgetIndex) => {
            if (!requiredString(widget?.id)) {
              errors.push(`sections[${sectionIndex}].widgets[${widgetIndex}].id is required`);
            }
            if (!requiredString(widget?.type)) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}].type is required`,
              );
            } else if (!KNOWN_LIST_HEADER_WIDGET_TYPES.has(widget.type)) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}] has unknown type "${widget.type}"`,
              );
            }
            if (
              widget?.type === "sort" &&
              (!Array.isArray(widget.options) || widget.options.length === 0)
            ) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}].options must be a non-empty array`,
              );
            }
            if (
              widget?.type === "pagination" &&
              widget.pageSize != null &&
              (typeof widget.pageSize !== "number" || widget.pageSize <= 0)
            ) {
              errors.push(
                `sections[${sectionIndex}].widgets[${widgetIndex}].pageSize must be a positive number`,
              );
            }
            if (widget?.type === "bulkActions" && Array.isArray(widget.actions)) {
              widget.actions.forEach((action, actionIndex) => {
                if (!requiredString(action?.actionKey)) {
                  errors.push(
                    `sections[${sectionIndex}].widgets[${widgetIndex}].actions[${actionIndex}].actionKey is required`,
                  );
                } else if (!KNOWN_BULK_ACTION_KEYS.has(action.actionKey)) {
                  errors.push(
                    `sections[${sectionIndex}].widgets[${widgetIndex}].actions[${actionIndex}] has unknown actionKey "${action.actionKey}"`,
                  );
                }
              });
            }
          });
        }
      }
    });
  }

  return { valid: errors.length === 0, errors };
}
